import {useState} from 'react';

import aemClientService from '@/services/aem/aemClientService';
import type {ImageModel} from '@/types/aem';
import arrayUtils from '@/utils/arrayUtils';
import {twx} from '@/utils/tailwindUtils';

type GroupedMedia = {
    key: string;
    item: ImageModel[];
};

const getGroupedImageMedia = (imageMedia: ImageModel[]): GroupedMedia[] => {
    const mapped = imageMedia.map((x) => ({...x, width: x.viewport?.width ?? 0}));
    const grouped = arrayUtils.groupBy(mapped, 'width');
    return arrayUtils.objectToArray(grouped);
};

const isDefaultMedia = (item: GroupedMedia) => item.key === '0';

const getImageSrcSet = (media: ImageModel[]) =>
    media
        .map((image) => {
            const srcPath = aemClientService.getAssetPath(image.path);
            return `${srcPath} ${image.ppi ?? 1}x`;
        })
        .join(', ');

type AemImageProps = {
    imageMedia?: ImageModel[];
    className?: string;
    imgClassName?: string;
};

/**
 * Renders a responsive AEM-authored image using <picture> with srcset.
 * Supports multiple viewports and pixel densities from ImageModel[].
 */
const AemImage = ({imageMedia, className, imgClassName}: AemImageProps) => {
    const [hasError, setHasError] = useState(false);

    if (!imageMedia?.length || hasError) return null;

    const groupedImageMedia = getGroupedImageMedia(imageMedia);
    const defaultGroup = groupedImageMedia.find(isDefaultMedia);
    const defaultImages = defaultGroup?.item;

    if (!defaultImages?.length) return null;

    const otherMedia = groupedImageMedia
        .filter((x) => !isDefaultMedia(x))
        .sort((a, b) => Number(b.key) - Number(a.key));

    const defaultImage = defaultImages[0];

    return (
        <picture className={className}>
            {otherMedia.map(({key, item}) => {
                if (!item?.length) return null;
                const maxWidth = item[0]?.viewport?.maxWidth;
                const mediaQuery = maxWidth
                    ? `(min-width: ${key}px) and (max-width: ${maxWidth}px)`
                    : `(min-width: ${key}px)`;
                return <source key={key} srcSet={getImageSrcSet(item)} media={mediaQuery} />;
            })}
            <img
                className={twx('h-auto w-full', imgClassName)}
                src={aemClientService.getAssetPath(defaultImage.path)}
                srcSet={getImageSrcSet(defaultImages)}
                alt=''
                onError={() => setHasError(true)}
            />
        </picture>
    );
};

export default AemImage;
